import { useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * Modal shown when a client marker is clicked on the clients map.
 * @param {boolean} isOpen
 * @param {function} onClose
 * @param {object|null} cliente - Client row from lista (descripcion, codigo, latitud, longitud, …)
 */
export default function ModalClienteMapa({ isOpen, onClose, cliente = null }) {
    const { t } = useTranslation();
    const modalRef = useRef(null);

    if (!isOpen || !cliente) {
        return null;
    }

    const handleOutsideClick = (e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
            onClose();
        }
    };

    const titulo = cliente.descripcion || cliente.codigo || t("label_detalle_cliente_en_mapa");
    const color = cliente.color && String(cliente.color).trim() !== "" ? cliente.color : "#6b7280";

    return (
        <div
            className="fixed z-[70] inset-0 flex items-center justify-center p-4"
            onMouseDown={handleOutsideClick}
        >
            <div className="absolute inset-0 bg-black/10" />

            <div
                ref={modalRef}
                className="relative bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] flex flex-col"
            >
                <div className="p-5 border-b flex items-start justify-between gap-3">
                    <div className="min-w-0 flex items-start gap-3">
                        <span
                            className="mt-1 h-4 w-4 shrink-0 rounded border border-gray-200"
                            style={{ backgroundColor: color }}
                            aria-hidden
                        />
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold text-gray-900 truncate">
                                {titulo}
                            </h2>
                            {cliente.codigo != null && cliente.codigo !== "" && (
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {t("label_codigo")}: {cliente.codigo}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-5 overflow-y-auto text-sm text-gray-700 space-y-3">
                    <dl className="grid grid-cols-[minmax(0,8.5rem)_1fr] gap-x-3 gap-y-2">
                        {cliente.tipoCliente != null && cliente.tipoCliente !== "" && (
                            <>
                                <dt className="text-gray-500">{t("label_tipo_cliente")}</dt>
                                <dd className="font-medium text-gray-900">{cliente.tipoCliente}</dd>
                            </>
                        )}
                        {cliente.ciudad != null && cliente.ciudad !== "" && (
                            <>
                                <dt className="text-gray-500">{t("label_ciudad")}</dt>
                                <dd className="font-medium text-gray-900">{cliente.ciudad}</dd>
                            </>
                        )}
                        {cliente.latitud != null && cliente.longitud != null && (
                            <>
                                <dt className="text-gray-500">{t("label_latitud")}</dt>
                                <dd className="font-mono tabular-nums text-gray-900">{cliente.latitud}</dd>
                                <dt className="text-gray-500">{t("label_longitud")}</dt>
                                <dd className="font-mono tabular-nums text-gray-900">{cliente.longitud}</dd>
                            </>
                        )}
                        {cliente.idGeocerca != null && cliente.idGeocerca !== "" && (
                            <>
                                <dt className="text-gray-500">{t("label_geocerca")}</dt>
                                <dd className="font-medium text-gray-900">{String(cliente.idGeocerca)}</dd>
                            </>
                        )}
                        {cliente.habilitadoLabel != null && cliente.habilitadoLabel !== "" && (
                            <>
                                <dt className="text-gray-500">{t("column_habilitado")}</dt>
                                <dd className="font-medium text-gray-900">{cliente.habilitadoLabel}</dd>
                            </>
                        )}
                        {cliente.fechaProceso != null && cliente.fechaProceso !== "" && (
                            <>
                                <dt className="text-gray-500">{t("label_fecha")}</dt>
                                <dd className="text-gray-900">{cliente.fechaProceso}</dd>
                            </>
                        )}
                    </dl>

                    <div className="pt-4 mt-2 border-t border-gray-100">
                        <p className="text-center text-gray-500 italic py-6">
                            {t("label_proximamente")}
                        </p>
                    </div>
                </div>

                <div className="p-4 border-t flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium text-gray-800"
                    >
                        {t("label_cerrar")}
                    </button>
                </div>
            </div>
        </div>
    );
}
